/* Program Name: UserLookUp.js
 **  Description: a modal for displaying the support and help information for users
 **
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2021-05-14  STT  Initial program
 ** IS200324   2023-08-08  TJS  Added profile default when proxied
 ** SD6391     2024-11-04  SGS  Prevent Unncessary call to user/lookup_rdr
 ** SD6391     2024-11-27  SGS  Display proxy-target cart cnt after proxy connect
 ** SD6391     2024-12-17  SGS  Setup open/close of ProxyDetails modal
 ** SD9567     2026-02-02  SGS  ITSM-517 - Expose CC Expiration Dates for Call Center
 ** ITSM735    2026-10-08  SGS  Added view-only Messages tab for user-level messages
 *********************************************************************************/

/**React imports */
import React, { useEffect, useRef, useState, useCallback } from 'react'

/** React Router imports */
import { useHistory } from 'react-router'

/**ezPOP imports */
import Window from '../Window'
import ProxyDetails from './ProxyDetails'

/** API imports */
import LookUpByStoreNum from './LookUpByStoreNum'
import { FlexBox, FlexOne } from '../CustomDivs'
import BasicGrid from '../BasicGrid'
import {
  PrimaryButtonFlexWidth,
  SquarePrimaryButton,
} from '../ActionColorButtons'

/**MUI imports */
import {
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { CheckCircle, ContentCopy, PersonSearch } from '@mui/icons-material'
import { styled } from '@mui/system'

/** API imports */
import {
  getUserLookup,
  getUserLookupList,
  getUserLookupListNoSeri,
  proxyUser,
  checkProxy,
} from '../../../API/generalAPI'
import { takeCart } from '../../Cart/cartAPI'

/** Context imports */
import { useAlert } from '../../../Context/AlertContext'
import { useCart } from '../../../Context/CartContext'
import { useLoading } from '../../../Context/LoadingContext'
import { useProxy } from '../../../Context/ProxyContext'

/** columns */
import { columns } from './userLookUpColumns'

/**react copy to clipboard imports */
import CopyToClipboard from 'react-copy-to-clipboard'
import TabPanel from '../TabPanel'

/**Active************************************************** */
//Styled Button to respresent active users
const Active = styled(Button)(({ theme }) => ({
  '&:disabled': {
    color: theme.palette.success.light + 'CC',
  },
}))

/**Inactive************************************************** */
//Styled Button to respresent inactive users
const Inactive = styled(Button)(({ theme }) => ({
  '&:disabled': {
    color: theme.palette.error.light + 'CC',
  },
}))

/**Role************************************************** */
//Styled Button to respresent users roles
const Role = styled(Button)(({ theme }) => ({
  '&:disabled': {
    color: theme.palette.secondary.main + 'CC',
  },
}))

/**allyProps************************************************** */
//function to set the appropriate id to selected TabPanel
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

/**UserLookUp ***************************************************************/
//open  => bool => boolean that determines if the modal is open
//close => func => function that sets open to false
const UserLookUp = ({ open, close, getMetaData }) => {
  /****************************************************************************/
  /**Local Variables */
  //Context end point to use the application alert system
  const alert = useRef(useAlert())
  //Context end point to start and stop loading screen
  const loading = useRef(useLoading())
  //Context end point that controls proxy related actions
  const proxy = useProxy()
  //Context end point to fire cart tally in header
  const { getCartCnt } = useCart()
  //Context end point to connect to react router tools
  const history = useHistory()
  //Stores the value of the the selected user
  const [selectedUser, setSelectedUser] = useState(null)
  //Stores a list of all the user
  const [users, setUsers] = useState([])
  //Stores a list of all the roles
  const [roles, setRoles] = useState([])
  //Stores a list of all the stores
  const [stores, setStores] = useState([])
  //Stores a list of all the carts
  const [carts, setCarts] = useState([])
  //Stores a list of all the credit cards
  const [cards, setCards] = useState([])
  //Stores a list of the user's messages
  const [messages, setMessages] = useState([])
  //Stores the value of the currently selected tab
  const [value, setValue] = useState(0)
  //Stores a boolean to determine if Serigraph users are shown or not
  const [seriUser, setSeriUser] = useState(false)
  //Stores a boolean to determine if non proxiable targets are shown or not
  const [proxyTar, setProxyTar] = useState(true)
  //Stores a boolean to determine if users from all domains are shown
  const [allDomain, setAllDomain] = useState(false)
  //Stores the value of the current type of search that is being used
  const [searchType, setSearchType] = useState(0)
  //Stores a boolean to determine if the user info was copied to the clipboard or not
  const [copied, setCopied] = useState(false)
  //Stores a boolean to determine if the lookup was complete or not
  const [lookupComplete, setLookupComplete] = useState(false)
  //Stores context to open and close proxy details modal
  const [modal, setModal] = useState(false)

  /******************************************************************************/
  /**Handle Change*/
  /******************************************************************************/
  /** sets the value of the selected tab to state in order to change the tab
   *  * @param {string} newValue - the value of the selected tab
   */
  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  /******************************************************************************/
  /**Close Modal*/
  /******************************************************************************/
  /** sets the state of the modal to closed
   *  * @param {bool} newValue - the value of the modals visibility
   */
  const closeModal = (bool) => {
    setModal(bool)
  }

  /******************************************************************************/
  /**Handle Search Type Change*/
  /******************************************************************************/
  /** sets the value of the search type
   *  * @param {string} newValue - the value of the new search type
   */
  const handleSearchTypeChange = (event, newValue) => {
    setSearchType(newValue)
  }

  /******************************************************************************/
  /**Handle Checked*/
  /******************************************************************************/
  /** sets the value serigraph user checkbox and makes the reader call for the
   * appropriate list of users
   */
  const handleChecked = (e) => {
    setSeriUser(e.target.checked)
    if (searchType === 1) {
      if (e.target.checked) {
        getUsers()
      } else {
        getUsersNoSeri()
      }
    }
  }

  /******************************************************************************/
  /**Handle Domain Checked*/
  /******************************************************************************/
  /** sets the value of allDomains and get a list of users based of off appropriate
   * options
   */
  const handleDomainChecked = (e) => {
    setAllDomain(e.target.checked)
    var options
    if (e.target.checked) {
      options = `${proxyTar ? 'ProxyTargets' : ''}`
    } else {
      options = `DefaultDomain,${proxyTar ? 'ProxyTargets' : ''}`
    }
    getUsers(options)
  }

  /******************************************************************************/
  /**Handle Proxy Tar Checked*/
  /******************************************************************************/
  /** sets the value of proxyTar and gets a list of users based of off appropriate
   * options
   */
  const handleProxyTarChecked = (e) => {
    setProxyTar(e.target.checked)
    var options
    if (e.target.checked) {
      options = `ProxyTargets,${allDomain ? '' : 'DefaultDomain'}`
    } else {
      options = `${allDomain ? '' : 'DefaultDomain'}`
    }
    getUsers(options)
  }

  /******************************************************************************/
  /**Handle User Select*/
  /******************************************************************************/
  /** makes a call to get the details of the user provided to the function
   *  * @param {int} id - id of the user
   */
  const handleUserSelect = (id) => {
    getUserDetail(id)
  }

  /******************************************************************************/
  /**Handle Back*/
  /******************************************************************************/
  /** remove selectedUser
   */
  const handleBack = () => {
    setSelectedUser(null)
  }

  /******************************************************************************/
  /**Handle Take Cart*/
  /******************************************************************************/
  /** hit the db to handle current user taking over control of the selected cart
   *  * @param {int} id - id of the cart
   */
  const handleTakeCart = (id) => {
    loading.current.startLoading()
    takeCart(id)
      .then((response) => {
        alert.current.postAjaxAlert(response.data.meta[0])
      })
      .catch((error) => {
        alert.current.errorMessage(error.message)
      })
      .then(() => {
        getUserDetail(selectedUser.id)
        getCartCnt()
      })
  }

  /******************************************************************************/
  /**Handle Cart Errors*/
  /******************************************************************************/
  /** makes a call to get the details of the user provided to the function
   *  * @param {int} id - id of the number
   */
  const handleCartErrors = (id, origin) => {
    history.push(`/carterrors/${id}/${origin}`)
    close(false)
  }

  /******************************************************************************/
  /**Handle Proxy*/
  /******************************************************************************/
  /** sets the current user to "proxy" the selected user
   */
  const handleProxy = (takeUserCart) => {
    loading.current.startLoading()
    proxyUser(selectedUser.id, takeUserCart)
      .then((response) => {
        alert.current.postAjaxAlert(response.data.meta[0])
        
        loading.current.stopLoading()
        handleProxyHelper()
      })
      .catch((error) => {
        alert.current.errorMessage(error.message)
      })
  }

  /******************************************************************************/
  /**Handle Proxy Helper*/
  /******************************************************************************/
  /** sets the appropriate state for isolated components after proxy connection
   */
  const handleProxyHelper = () => {
    getMetaData()
    getCartCnt()

    history.push('/')
    proxy.stateReload()

    close(false)
    handleClose()
  }

  /******************************************************************************/
  /**Handle Close*/
  /******************************************************************************/
  /** resets the state of lookupComplete and seriUser
   */
  const handleClose = () => {
    setLookupComplete(false)
    setSeriUser(false)
  }

  /******************************************************************************/
  /**Handle Open*/
  /******************************************************************************/
  /** handles the functions that fire when opening the window.
   * checkProxy call its determining if the current user is proxied or not
   *    if proxied the window will display the proxied users info immediately
   */
  const handleOpen = () => {
    checkProxy()
      .then((response) => {
        if (!response.data.meta[0].err) {
          //alert.current.postAjaxAlert(response.data.meta[0])
          if (response.data.ttUser) {
            //console.log(response.data.ttUser)
            setSelectedUser(response.data.ttUser[0])
            if (response.data.ttUserRole) {
              setRoles(response.data.ttUserRole)
            } else {
              setRoles([])
            }
            if (response.data.ttLocation) {
              setStores(response.data.ttLocation)
            } else {
              setStores([])
            }
            if (response.data.ttStoredCard) {
              setCards(response.data.ttStoredCard)
            } else {
              setCards([])
            }
            if (response.data.ttUserCarts) {
              setCarts(response.data.ttUserCarts)
            } else {
              setCarts([])
            }
            // console.log("1")
          } else {
            // console.log("2")
            setSearchType(0)
            setSelectedUser(null)
          }
          // console.log("3")
          setLookupComplete(true)
        } else {
          alert.current.postAjaxAlert(response.data.meta[0])
        }
      })
      .catch((error) => {
        alert.current.errorMessage(error.message)
      })
      .then(() => {})
    // console.log(Header.name)
  }

  /** Get =>
   * grabs all of the UserRegs from the db using getUserRegs() from userRegAPI.js
   * handles any errors and the loading modal
   */
  const getUserDetail = useCallback((id) => {
    loading.current.startLoading()
    getUserLookup(id)
      .then((response) => {
        if (!response.data.meta[0].err) {
          setCopied(false)
          if (response.data.ttUser) {
            setSelectedUser(response.data.ttUser[0])
            // setProxyUser(response.data.ttUser[0])
          }
          if (response.data.ttUserRole) {
            setRoles(response.data.ttUserRole)
          } else {
            setRoles([])
          }
          if (response.data.ttLocation) {
            setStores(response.data.ttLocation)
          } else {
            setStores([])
          }
          if (response.data.ttStoredCard) {
            setCards(response.data.ttStoredCard)
          } else {
            setCards([])
          }
          if (response.data.ttUserCarts) {
            setCarts(response.data.ttUserCarts)
          } else {
            setCarts([])
          }
          if (response.data.ttUserMessage) {
            setMessages(response.data.ttUserMessage)
          } else {
            setMessages([])
          }
        } else {
          alert.current.postAjaxAlert(response.data.meta[0])
        }
      })
      .catch((error) => {
        alert.current.errorMessage(error.message)
      })
      .then(() => {
        loading.current.stopLoading()
      })
  }, [])

  /** Get =>
   * grabs all of the UserRegs from the db using getUserRegs() from userRegAPI.js
   * handles any errors and the loading modal
   */
  const getUsers = useCallback((options) => {
    loading.current.startLoading()
    getUserLookupList(options)
      .then((response) => {
        if (!response.data.meta[0].err) {
          if (response.data.ttUser) {
            setUsers(response.data.ttUser)
          } else {
            setUsers([])
          }
        } else {
          alert.current.postAjaxAlert(response.data.meta[0])
        }
      })
      .catch((error) => {
        alert.current.errorMessage(error.message)
      })
      .then(() => {
        loading.current.stopLoading()
      })
  }, [])

  /** Get =>
   * grabs all of the UserRegs from the db using getUserRegs() from userRegAPI.js
   * handles any errors and the loading modal
   */
  const getUsersNoSeri = useCallback(() => {
    loading.current.startLoading()
    getUserLookupListNoSeri()
      .then((response) => {
        if (!response.data.meta[0].err) {
          if (response.data.ttUser) {
            setUsers(response.data.ttUser)
          }
        } else {
          alert.current.postAjaxAlert(response.data.meta[0])
        }
      })
      .catch((error) => {
        alert.current.errorMessage(error.message)
      })
      .then(() => {
        loading.current.stopLoading()
      })
  }, [])

  /** Grid Columns */
  // sets any nessacary action buttons to the columns for the grid
  const gridColumns = [
    {
      headerName: 'Actions',
      field: ' ',
      width: 120,
      resizable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <FlexBox>
            <Tooltip disableInteractive title={'Click to View Details'}>
              <SquarePrimaryButton
                onClick={() => {
                  handleUserSelect(params.row.id)
                }}
              >
                <PersonSearch />
              </SquarePrimaryButton>
            </Tooltip>
          </FlexBox>
        )
      },
    },
    ...columns,
  ]

  /****************************************************************************/
  /**Use Effect */

  useEffect(() => {
    if (open) {
      setUsers([])
      if (searchType === 1) {
        getUsers(
          `${proxyTar ? 'ProxyTargets' : 'a'},${
            allDomain ? '' : 'DefaultDomain'
          }`
        )
      }
    }
  }, [open, getUsers, searchType])

  useEffect(() => {
    if (open && !lookupComplete) {
      handleOpen()
    }
  }, [open, lookupComplete])

  /****************************************************************************/
  /**Render */

  return lookupComplete ? (
    <Window
      noContainer
      width={'md'}
      open={open}
      close={() => {
        setSearchType(0)
        handleClose()
        close(false)
      }}
      error={selectedUser ? 'Back' : null}
      handleError={handleBack}
      title={'User Lookup'}
    >
      <div>
        {selectedUser ? (
          <div style={{ margin: '32px 16px 32px 16px' }}>
            <FlexBox style={{ width: '100%' }}>
              <div
                style={{
                  width: 75,
                  height: 75,
                  backgroundColor: '#EFEFEF',
                  borderRadius: 360,
                  textAlign: 'center',
                  lineHeight: '75px',
                  fontSize: '36px',
                  fontWeight: 600,
                }}
              >
                {selectedUser.first.charAt(0)}
                {selectedUser.last.charAt(0)}
              </div>
              <FlexOne style={{ marginLeft: '8px' }}>
                <FlexBox>
                  <Typography variant="h6">{`${selectedUser.first} ${selectedUser.last}`}</Typography>
                  <div
                    style={{
                      lineHeight: '32px',
                      marginLeft: '8px',
                      marginRight: '8px',
                    }}
                  >{` - ${selectedUser.login}`}</div>
                  <CopyToClipboard
                    text={`User ID: ${selectedUser.login} \nUsername: ${selectedUser.dispName} \nEmail: ${selectedUser.email}`}
                    onCopy={() => {
                      setCopied(true)
                    }}
                  >
                    <FlexBox>
                      <ContentCopy size={'sm'} sx={{ cursor: 'pointer' }} />{' '}
                      {copied ? <div>copied</div> : <div>copy</div>}
                    </FlexBox>
                  </CopyToClipboard>
                  <FlexOne />
                  {selectedUser.active ? (
                    <Active
                      variant="outlined"
                      disabled
                      color="success"
                      endIcon={<CheckCircle />}
                    >
                      Active
                    </Active>
                  ) : (
                    <Inactive variant="outlined" disabled color="error">
                      Inactive
                    </Inactive>
                  )}
                </FlexBox>
                <FlexBox>
                  <FlexBox>
                    {roles.length > 0 ? (
                      roles.map((role) => {
                        return (
                          <Role size="small" variant="outlined" disabled>
                            {role.label}
                          </Role>
                        )
                      })
                    ) : (
                      <Inactive variant="contained" color="error" disabled>
                        No Roles
                      </Inactive>
                    )}
                  </FlexBox>
                  <div style={{ lineHeight: '32px', marginLeft: '8px' }}>
                    {selectedUser.email}
                  </div>
                  <FlexOne />
                  <PrimaryButtonFlexWidth
                    sx={{ margin: '4px 0px 0px 0px' }}
                    onClick={() => setModal(true)}
                  >
                    Proxy For User
                  </PrimaryButtonFlexWidth>
                </FlexBox>
              </FlexOne>
            </FlexBox>
            <Box sx={{ width: '100%', marginTop: '32px' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="basic tabs example"
                >
                  <Tab label="Stores" {...a11yProps(0)} />
                  <Tab label="Carts" {...a11yProps(1)} />
                  <Tab label="Credit Cards" {...a11yProps(2)} />
                  <Tab label="Messages" {...a11yProps(3)} />
                </Tabs>
              </Box>
              <TabPanel value={value} index={0}>
                <BasicGrid
                  gridTitle={'Stores'}
                  gridData={stores}
                  realColumns={[
                    { headerName: 'Code', field: 'locCode', width: 250 },
                    { headerName: 'Name', field: 'locName', width: 175 },
                  ]}
                  rowStyles={{ height: 300 }}
                />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <BasicGrid
                  gridTitle={'Carts'}
                  gridData={carts}
                  realColumns={[
                    {
                      headerName: 'Actions',
                      field: ' ',
                      width: 200,
                      resizable: false,
                      sortable: false,
                      filterable: false,
                      renderCell: (params) => {
                        return (
                          <FlexBox>
                            <div style={{ marginLeft: 6, marginRight: 6 }}>
                              <Tooltip
                                disableInteractive
                                title={'Click to Take Cart'}
                              >
                                <Button
                                  onClick={() => {
                                    handleTakeCart(params.row.id)
                                  }}
                                >
                                  Take Cart
                                </Button>
                              </Tooltip>
                            </div>
                            <div style={{ marginLeft: 6, marginRight: 6 }}>
                              <Tooltip
                                disableInteractive
                                title={'Click to View Cart Errors'}
                              >
                                <Button
                                  color="error"
                                  onClick={() => {
                                    handleCartErrors(params.row.id, 'UserLookUp')
                                  }}
                                >
                                  Errors
                                </Button>
                              </Tooltip>
                            </div>
                          </FlexBox>
                        )
                      },
                    },
                    { headerName: 'Active', field: 'active', width: 250 },
                    {
                      headerName: 'Additional Emails',
                      field: 'emails',
                      width: 175,
                    },
                    { headerName: 'Time', field: 'timem', width: 175 },
                    {
                      headerName: 'Total Lines',
                      field: 'totLines',
                      width: 175,
                    },
                    { headerName: 'Total Loc', field: 'totLoc', width: 175 },
                    { headerName: 'Total Qty', field: 'totQty', width: 175 },
                  ]}
                  rowStyles={{ height: 300 }}
                />
              </TabPanel>
              <TabPanel value={value} index={2}>
                <BasicGrid
                  gridTitle={'Credit Cards'}
                  gridData={cards}
                  realColumns={[
                    { headerName: 'Type', field: 'type', width: 250 },
                    { headerName: 'Last Four', field: 'lastFour', width: 175 },
                    { headerName: 'Name', field: 'name', width: 175 },
                    { headerName: 'Expires', field: 'exp', width: 175 },
                    { headerName: 'Active', field: 'active', width: 175 }
                  ]}
                  rowStyles={{ height: 300 }}
                />
              </TabPanel>
              <TabPanel value={value} index={3}>
                <BasicGrid
                  gridTitle={'Messages'}
                  gridData={messages}
                  realColumns={[
                    { headerName: 'Created', field: 'startDate', width: 175 },
                    { headerName: 'Message', field: 'message', width: 600 },
                  ]}
                  rowStyles={{ height: 300 }}
                />
              </TabPanel>
            </Box>
          </div>
        ) : (
          <div>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={searchType}
                onChange={handleSearchTypeChange}
                aria-label="basic tabs example"
              >
                <Tab label="Search by Store Number" {...a11yProps(0)} />
                <Tab label="Search by User" {...a11yProps(1)} />
              </Tabs>
            </Box>
            <TabPanel value={searchType} index={0}>
              <LookUpByStoreNum
                getUserDetail={getUserDetail}
                seriUser={seriUser}
                handleChecked={handleChecked}
              />
            </TabPanel>
            <TabPanel value={searchType} index={1}>
              <div style={{ height: '64px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allDomain}
                      onChange={handleDomainChecked}
                      name="rememberMe"
                    />
                  }
                  label={<p>Show Serigraph Users</p>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={proxyTar}
                      onChange={handleProxyTarChecked}
                      name="rememberMe"
                    />
                  }
                  label={<p>Show Proxyable Targets</p>}
                />
              </div>
              <BasicGrid
                gridTitle={''}
                gridData={users}
                realColumns={gridColumns}
                rowStyles={{ height: 600 }}
              />
            </TabPanel>
          </div>
        )}
      </div>
      <ProxyDetails
        open={modal}
        closeModal={closeModal}
        handleProxy={(takeUserCart) => handleProxy(takeUserCart)}
        selectedUser={selectedUser}
      />
    </Window>
  ) : (
    false
  )
}

export default UserLookUp
